<template>
  <div>
    <FieldLabel :label="label" :tooltip="tooltip" :disabled="disabled" :required="effectiveRequired" :fieldKey="fieldKey" />

    <v-input
      v-if="choiceVariant !== 'dropdown'"
      ref="selectField"
      :model-value="validationValue"
      :rules="computedRules"
      :disabled="disabled"
      :readonly="readonly"
      :focused="isFocused"
      hide-details="auto"
      density="compact"
      class="q-choice"
    >
      <template #default="{ isValid, messagesId }">
        <div
          v-if="choiceVariant === 'segments'"
          class="q-seg"
          role="radiogroup"
          :aria-label="plainLabel"
          :aria-required="effectiveRequired && !disabled ? 'true' : undefined"
          :aria-readonly="readonly ? 'true' : undefined"
          :aria-disabled="disabled ? 'true' : undefined"
          :aria-invalid="isValid.value === false ? 'true' : undefined"
          :aria-describedby="messagesId.value"
          @keydown="onSegmentKeydown"
          @focusin="isFocused = true"
          @focusout="onChoiceFocusOut"
        >
          <button
            v-for="(option, index) in choiceOptions"
            :key="option.value"
            type="button"
            role="radio"
            class="q-seg__btn"
            :class="{ 'q-seg__btn--on': isChoiceSelected(option) }"
            :aria-checked="isChoiceSelected(option) ? 'true' : 'false'"
            :tabindex="index === segmentTabStop ? 0 : -1"
            :disabled="disabled || option.disabled"
            @click="selectChoice(option)"
          >
            {{ $gt(option.title) }}
          </button>
        </div>

        <div
          v-else
          class="q-chips"
          role="group"
          :aria-label="plainLabel"
          :aria-disabled="disabled ? 'true' : undefined"
          :aria-invalid="isValid.value === false ? 'true' : undefined"
          :aria-describedby="messagesId.value"
          @keydown.delete="clearChoice"
          @focusin="isFocused = true"
          @focusout="onChoiceFocusOut"
        >
          <button
            v-for="option in choiceOptions"
            :key="option.value"
            type="button"
            class="q-chipbtn"
            :class="{ 'q-chipbtn--on': isChoiceSelected(option) }"
            :aria-pressed="isChoiceSelected(option) ? 'true' : 'false'"
            :disabled="disabled || option.disabled"
            @click="toggleChoice(option)"
          >
            <Check v-if="isChoiceSelected(option)" class="q-chipbtn__check" aria-hidden="true" />
            {{ $gt(option.title) }}
          </button>
        </div>
      </template>
    </v-input>

    <v-select
      v-else
      ref="selectField"
      v-model="localValue"
      :disabled="disabled"
      :readonly="readonly"
      :placeholder="hasPlaceholder && !readonly ? $gt('Bitte auswählen') : ''"
      :items="computedItems"
      :item-value="computedItemValue"
      :item-title="computedItemTitle"
      :no-data-text="$gt(noDataText)"
      :chips="chips"
      :multiple="chips"
      :prepend-inner-icon="prependIcon"
      :required="effectiveRequired"
      :rules="computedRules"
      :hide-details="hideDetails ? true : 'auto'"
      color="primary"
      variant="outlined"
      density="compact"
      :class="['my-select', { 'my-select--readonly': readonly }]"
      autocomplete="new-password"
      @keydown.delete="clearSelection"
      @keydown.backspace="clearSelection"
    >
      <template #item="{ props, item }">
        <ListItem
          v-bind="props"
          :title="$gt(item.raw[computedItemTitle] ?? item.title ?? item.raw)"
          :icon="item.raw.icon"
          :iconType="item.raw.iconType"
          :disabled="item.raw.disabled"
        />
      </template>

      <template #selection="{ item }">
        <div class="d-flex align-center my-select-selection">
          <component v-if="item.raw.iconType || item.raw.icon" :is="getIcon(item.raw.icon, item.raw.iconType)" class="list-item-icon" />
          <span class="list-item-title">
            {{ $gt(item.raw[computedItemTitle] ?? item.title ?? item.raw) }}
          </span>
        </div>
      </template>

      <template #no-data>
        <ListItem :title="$gt('Keine Daten vorhanden')" :disabled="true" />
      </template>
    </v-select>
  </div>
</template>

<script>
import FieldLabel from "@/components/inputs/FieldLabel.vue";
import ListItem from "@/components/utils/ListItem.vue";
import { clearMarker } from "@/stores/caseStore/previewMeta";
import * as validationRules from "@/utils/validationRules.js";
import { getIcon } from "@/components/utils/utils";

import companyPrivate from "./data/companyPrivate.json";
import countries from "./data/countries.json";
import languages from "./data/languages.json";
import salutations from "./data/salutations.json";
import titles from "./data/titles.json";
import subcategoriesVehicle from "./data/subcategoriesVehicle.json";
import subcategoriesObject from "./data/subcategoriesObject.json";
import subcategoriesOther from "./data/subcategoriesOther.json";
import subcategoryTractorUnit from "./data/subcategoriesTractorUnit.json";
import subcategoryTrailer from "./data/subcategoriesTrailer.json";
import relations from "./data/relations.json";
import manufacturers from "./data/manufacturers.json";
import userRolesLawFirm from "./data/userRolesLawFirm.json";
import legalForms from "./data/legalForms.json";
import sectionFieldMixin from "@/pages/pageQuestionnaire/base/sectionFieldMixin.js";
import { Check } from "lucide-vue-next";
import { nextEnabledIndex, resolveChoiceVariant, toChoiceOptions, toggleChoiceValue } from "./choiceVariant";

const SEGMENT_KEY_DIRECTION = Object.freeze({ ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 });

export default {
  name: "Select",

  mixins: [sectionFieldMixin],

  components: {
    FieldLabel,
    ListItem,
    Check,
  },

  inject: {
    parentKey: { default: null },
    groupId: { default: null },
    // Nur im Unfallfragebogen („geführte Etappen“) gesetzt; sonst Dropdown wie bisher.
    questionnaireLayout: { default: null },
  },

  props: {
    label: {
      type: String,
      required: true,
    },
    tooltip: {
      type: String,
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    clearOnDisable: {
      type: Boolean,
      default: true,
    },
    items: {
      type: Array,
      default: () => [],
    },
    itemValue: {
      type: String,
      default: null,
    },
    itemTitle: {
      type: String,
      default: null,
    },
    noDataText: {
      type: String,
      default: "Keine Daten vorhanden",
    },
    chips: {
      type: Boolean,
      default: false,
    },
    rules: {
      type: Array,
      default: () => [],
    },
    hideDetails: {
      type: Boolean,
      default: true,
    },
    required: {
      type: Boolean,
      default: false,
    },
    prependIcon: {
      type: String,
      default: "",
    },
    modelValue: {
      type: [String, Number, Array, null],
      default: null,
    },
    type: {
      type: String,
      default: "",
    },
    hasPlaceholder: {
      type: Boolean,
      default: true,
    },
    fieldKey: {
      type: String,
      default: "",
    },
  },

  emits: ["update:modelValue", "blur"],

  data() {
    return {
      localValue: this.modelValue,
      // Guided: Fokus liegt im Umschalter/in den Chips (für Validierung beim Verlassen).
      isFocused: false,
    };
  },

  watch: {
    modelValue(newValue) {
      this.localValue = newValue;
      clearMarker(this.parentKey, this.groupId, this.fieldKey, this.localValue);
      // Re-run validation so error styling clears after programmatic updates
      // (e.g. country autofill from Google Places address autocomplete).
      this.$nextTick(() => {
        this.$refs.selectField?.validate();
      });
    },

    localValue(newValue) {
      this.$emit("update:modelValue", newValue);
    },

    disabled(newVal) {
      if (newVal && this.clearOnDisable) {
        this.localValue = null;
        this.$emit("update:modelValue", null);
      }
    },
  },

  computed: {
    computedItems() {
      if (this.items.length) return this.items;

      switch (this.type) {
        case "companyPrivate":
          return companyPrivate;
        case "country":
          return countries;
        case "languages":
          return languages;
        case "salutation":
          return salutations;
        case "title":
          return titles;
        case "subcategoryVehicle":
          return subcategoriesVehicle;
        case "subcategoryObject":
          return subcategoriesObject;
        case "subcategoryOther":
          return subcategoriesOther;
        case "subcategoryTractorUnit":
          return subcategoryTractorUnit;
        case "subcategoryTrailer":
          return subcategoryTrailer;
        case "relation":
          return relations;
        case "manufacturer":
          return manufacturers;
        case "userRoleLawFirm":
          return userRolesLawFirm;
        case "legalForm":
          return legalForms;
        default:
          return [];
      }
    },

    computedItemValue() {
      if (this.itemValue) return this.itemValue;
      if (this.items.length) return null;
      return "name";
    },

    computedItemTitle() {
      if (this.itemTitle) return this.itemTitle;
      if (this.items.length) return null;
      return "name";
    },

    computedRules() {
      if (this.readonly) return [];
      let finalRules = [...this.rules];
      if (this.effectiveRequired && !this.disabled) {
        finalRules.unshift(validationRules.requiredRule());
      }
      return finalRules;
    },

    // ── Guided-Layout: Umschalter bzw. Chips statt Dropdown (Spec §3) ──
    choiceOptions() {
      if (this.questionnaireLayout !== "guided") return null;
      return toChoiceOptions({
        items: this.computedItems,
        valueKey: this.computedItemValue,
        titleKey: this.computedItemTitle,
        // Objekte nur aus den festen Katalogen (type), nicht aus dem items-Prop.
        allowObjects: !this.items.length,
      });
    },

    choiceVariant() {
      return resolveChoiceVariant({
        guided: this.questionnaireLayout === "guided",
        chips: this.chips,
        options: this.choiceOptions,
      });
    },

    /** Leere Mehrfachauswahl zählt für die Pflichtprüfung als „nichts gewählt“. */
    validationValue() {
      if (Array.isArray(this.localValue) && this.localValue.length === 0) return null;
      return this.localValue;
    },

    /** Label ohne HTML für aria-label der Gruppe. */
    plainLabel() {
      return String(this.$gt(this.label) ?? "")
        .replace(/<[^>]*>/g, "")
        .trim();
    },

    /** Roving Tabindex: gewählte Option, sonst die erste aktivierbare. */
    segmentTabStop() {
      const options = this.choiceOptions ?? [];
      const selected = options.findIndex((option) => this.isChoiceSelected(option) && !option.disabled);
      if (selected !== -1) return selected;
      return options.findIndex((option) => !option.disabled);
    },
  },

  methods: {
    clearSelection(event) {
      this.localValue = null;
      this.$emit("update:modelValue", null);
      event.preventDefault();
    },
    getIcon,

    // ── Guided-Layout ──
    isChoiceSelected(option) {
      if (Array.isArray(this.localValue)) return this.localValue.includes(option.value);
      return this.localValue === option.value;
    },

    isChoiceLocked() {
      return this.disabled || this.readonly;
    },

    /** Einfachauswahl: eine andere Option ersetzt; erneuter Klick ändert nichts. */
    selectChoice(option) {
      if (this.isChoiceLocked() || option.disabled || this.isChoiceSelected(option)) return;
      this.localValue = option.value;
    },

    toggleChoice(option) {
      if (this.isChoiceLocked() || option.disabled) return;
      this.localValue = toggleChoiceValue(this.localValue, option.value);
    },

    /** Entf/Rücktaste leert die Auswahl – wie im Dropdown. */
    clearChoice(event) {
      event.preventDefault();
      if (this.isChoiceLocked()) return;
      this.localValue = null;
    },

    onSegmentKeydown(event) {
      // Tastenkürzel mit Modifikator (z. B. Alt+Pfeil = Browser-Zurück) nicht abfangen.
      if (event.altKey || event.ctrlKey || event.metaKey) return;

      if (event.key === "Delete" || event.key === "Backspace") {
        this.clearChoice(event);
        return;
      }

      const options = this.choiceOptions ?? [];
      const disabledFlags = options.map((option) => option.disabled);
      const buttons = Array.from(event.currentTarget.querySelectorAll("[role='radio']"));
      const current = buttons.indexOf(event.target);

      let target = -1;
      if (event.key in SEGMENT_KEY_DIRECTION) target = nextEnabledIndex(disabledFlags, current, SEGMENT_KEY_DIRECTION[event.key]);
      else if (event.key === "Home") target = nextEnabledIndex(disabledFlags, -1, 1);
      else if (event.key === "End") target = nextEnabledIndex(disabledFlags, -1, -1);
      else return;

      event.preventDefault();
      if (target === -1) return;

      // Wie bei Radiobuttons: Pfeiltaste bewegt den Fokus und wählt aus.
      buttons[target]?.focus();
      this.selectChoice(options[target]);
    },

    onChoiceFocusOut(event) {
      if (event.currentTarget.contains(event.relatedTarget)) return;
      this.isFocused = false;
    },
  },

  beforeUnmount() {
    this.localValue = null;
    clearMarker(this.parentKey, this.groupId, this.fieldKey, this.localValue);
    this.$emit("update:modelValue", null);
  },
};
</script>

<style scoped>
.my-select :deep(.v-field) {
  border-radius: 8px;
}

.my-select :deep(.v-field__input) {
  padding-top: 9px !important;
  padding-left: 12px !important;
  font-size: 0.9rem;
  border-radius: 8px;
}

.my-select :deep(.v-field) {
  padding-right: 8px !important;
}

.my-select--readonly {
  /* Meeting 27.07.26: kein grauer Hintergrund mehr ("wirkt nicht bearbeitbar"). */
  background-color: transparent;
}

.my-select--readonly :deep(.v-field__input) {
  user-select: text !important;
  cursor: text !important;
}

/* Selected item */
.my-select-selection :deep(.list-item-icon) {
  width: 20px;
  height: 20px;
  margin-right: 8px;
  stroke-width: 1.5;
}

.my-select-selection :deep(.list-item-title) {
  font-size: 0.9rem;
  color: var(--dark-color);
}
</style>
